import { NodeAxios } from '@/services/api'
import {
    dispatchCherylFixtureCommand,
    getCherylFixtureBatchDetail,
    getCherylFixtureBatchId,
    getCherylFixtureQueue,
    isCherylFixtureBatchId,
    isCherylFixtureReviewItems,
    isCherylKnowledgeReviewFixture,
} from '@/modules/interview-coach/knowledge-review-fixture'
import type { InterviewTypeUiOption } from '@/modules/interview-coach/interview-type-ui'
import type { ProfileFillCommandInput } from '@/modules/interview-coach/profile-fill-command.util'
import type {
    LiveInterviewDashboardSnapshot,
    DashboardStage,
    HarvestProfileCandidate,
} from '@/modules/interview-coach/dashboard-types'
import type {
    CaptureCandidate,
    InterviewBrief,
    InterviewGoal,
    InterviewPlanView,
    KeyPointsPage,
    LiveHandoffPackage,
    PlanAiEthnographicQuestion,
    PlanAiFetsPrompt,
    PlanAiFollowUp,
    PlanAiLadderingRung,
    PlanAiMeta,
    PlanAiProofGap,
    PlanAiSuggestedGoalDraft,
    ReviewQueueView,
} from '@/modules/interview-coach/plan-types'
import type {
    ConductorStepResult,
    DebugSnapshot,
    DialogueSummary,
    InterviewPanelSnapshot,
    LiveInterviewSession,
    LiveInterviewSessionSummary,
    LiveInterviewProfileContext,
    ZoomConnectionStatus,
    OnboardingResearchSeed,
} from './types'

export type GoldenCompileSubjectType = 'person' | 'company' | 'product' | 'group'

export type GoldenCompileTarget = {
    subjectId: string
    subjectType: GoldenCompileSubjectType
    subjectName?: string
}

export function resolveGoldenCompileTarget(
    session: Pick<LiveInterviewSessionSummary, 'libraryEntityId' | 'subjectKind' | 'profileSubjectName'>
): GoldenCompileTarget | null {
    const subjectId = session.libraryEntityId?.trim()
    const subjectType = session.subjectKind?.trim().toLowerCase()
    if (!subjectId || !subjectType || !['person', 'company', 'product', 'group'].includes(subjectType)) return null
    return {
        subjectId,
        subjectType: subjectType as GoldenCompileSubjectType,
        ...(session.profileSubjectName?.trim() ? { subjectName: session.profileSubjectName.trim() } : {}),
    }
}

/**
 * NodeAxios response interceptor already returns `response.data`.
 * Do not read `.data` again on the resolved value.
 */
export function pickOnboardingResearchFromSeedResponse(res: {
    onboardingResearch?: OnboardingResearchSeed
    plan?: InterviewPlanView
}): OnboardingResearchSeed | undefined {
    return res.onboardingResearch ?? res.plan?.onboardingResearch
}

function unwrapBody<T>(res: unknown): T {
    if (!res || typeof res !== 'object') return res as T

    const record = res as Record<string, unknown>

    // Full AxiosResponse slipped through (has status + nested data)
    if ('status' in record && 'data' in record && record.data !== undefined) {
        return record.data as T
    }

    // CommZone envelope: { success, data }
    if ('success' in record && record.data !== undefined && record.data !== null) {
        return record.data as T
    }

    return res as T
}

function extractSessionId(payload: unknown): string {
    const body = unwrapBody<Record<string, unknown>>(payload)
    if (!body || typeof body !== 'object') {
        throw new Error(`Session API returned non-object: ${String(payload)}`)
    }
    if (typeof body.sessionId === 'string' && body.sessionId) return body.sessionId
    const nested = body.data
    if (nested && typeof nested === 'object' && typeof (nested as Record<string, unknown>).sessionId === 'string') {
        return (nested as Record<string, string>).sessionId
    }
    throw new Error(`Session API unexpected shape: ${JSON.stringify(body).slice(0, 300)}`)
}

const HARVEST_PROFILES_CACHE_TTL_MS = 30_000
let harvestProfilesRequest: {
    promise: Promise<HarvestProfileCandidate[]>
    expiresAt: number
} | null = null
type KnowledgeReviewQueueResponse = import('@/modules/interview-coach/knowledge-review-types').KnowledgeReviewQueueResponse
const knowledgeReviewQueueRequests = new Map<string, Promise<KnowledgeReviewQueueResponse>>()

export const interviewCoachApi = {
    async getZoomStatus(): Promise<ZoomConnectionStatus> {
        const res = await NodeAxios.get('/integrations/zoom/status')
        const body = unwrapBody<{ success?: boolean; data?: ZoomConnectionStatus } & ZoomConnectionStatus>(res)
        if (
            body &&
            typeof body === 'object' &&
            body.data &&
            typeof body.data === 'object' &&
            'connected' in body.data
        ) {
            return body.data
        }
        return body as ZoomConnectionStatus
    },

    async disconnectZoom(sessionId: string): Promise<LiveInterviewSessionSummary> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/zoom/disconnect`)
        return unwrapBody(res)
    },

    async listInterviewTypes(channel = 'saas', scope: 'create' | 'setup' = 'create'): Promise<InterviewTypeUiOption[]> {
        const res = await NodeAxios.get(`/live-interviews/interview-types?channel=${channel}&scope=${scope}`)
        const body = unwrapBody<{ types: InterviewTypeUiOption[] }>(res)
        return body.types ?? []
    },

    async listSessions(opts?: {
        limit?: number
        status?: string
        profileId?: string
        libraryEntityId?: string
        subjectKind?: string
        includeArchived?: boolean
    }): Promise<LiveInterviewSessionSummary[]> {
        const params = new URLSearchParams()
        if (opts?.limit) params.set('limit', String(opts.limit))
        if (opts?.status) params.set('status', opts.status)
        if (opts?.profileId) params.set('profileId', opts.profileId)
        if (opts?.libraryEntityId) params.set('libraryEntityId', opts.libraryEntityId)
        if (opts?.subjectKind) params.set('subjectKind', opts.subjectKind)
        if (opts?.includeArchived) params.set('includeArchived', '1')
        const qs = params.toString()
        const res = await NodeAxios.get(`/live-interviews/sessions${qs ? `?${qs}` : ''}`)
        const body = unwrapBody<{ sessions: LiveInterviewSessionSummary[] }>(res)
        return body.sessions ?? []
    },
    async buildGoldenUnderstanding(
        sessionId: string,
        options: GoldenCompileTarget & { rebuild?: boolean; idempotencyKey?: string }
    ): Promise<{
        attemptId: string
        jobId: string
        status: string
        stage: string
        progress: { completed: number; total: number; label: string }
        idempotentReplay: boolean
        proposalOnly: boolean
    }> {
        const idempotencyKey =
            options?.idempotencyKey ?? `golden:${sessionId}${options?.rebuild ? `:retry:${Date.now()}` : ''}`
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${encodeURIComponent(sessionId)}/golden/compile`,
            {
                subjectId: options.subjectId,
                subjectType: options.subjectType,
                ...(options.subjectName ? { subjectName: options.subjectName } : {}),
                rebuild: options.rebuild === true,
                idempotencyKey,
            },
            { skipErrorToast: true, headers: { 'Idempotency-Key': idempotencyKey } }
        )
        return unwrapBody(res)
    },

    async cancelGoldenUnderstanding(attemptId: string): Promise<{ success: boolean; cancelled?: boolean }> {
        const res = await NodeAxios.post(
            `/campaigns/interview-compile/jobs/${encodeURIComponent(attemptId)}/cancel`,
            {},
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },

    async listKnowledgeReviewQueue(
        limit = 100,
        scope?: { subjectId?: string; subjectType?: string }
    ): Promise<KnowledgeReviewQueueResponse> {
        if (isCherylKnowledgeReviewFixture()) return getCherylFixtureQueue()
        const params = new URLSearchParams({ limit: String(limit) })
        if (scope?.subjectId) params.set('subjectId', scope.subjectId)
        if (scope?.subjectType) params.set('subjectType', scope.subjectType)
        const key = params.toString()
        const inFlight = knowledgeReviewQueueRequests.get(key)
        if (inFlight) return inFlight

        const promise = (async () => {
            const res = await NodeAxios.get(`/knowledge-review/queue?${key}`, {
                skipErrorToast: true,
            })
            return unwrapBody<KnowledgeReviewQueueResponse>(res)
        })()
        knowledgeReviewQueueRequests.set(key, promise)
        const clearInFlight = () => {
            if (knowledgeReviewQueueRequests.get(key) === promise) knowledgeReviewQueueRequests.delete(key)
        }
        void promise.then(clearInFlight, clearInFlight)
        return promise
    },

    async getKnowledgeReviewBatchDetail(
        batchId: string,
        scope?: { subjectId?: string; subjectType?: string }
    ): Promise<import('@/modules/interview-coach/knowledge-review-types').KnowledgeReviewBatchDetail> {
        if (isCherylKnowledgeReviewFixture() || isCherylFixtureBatchId(batchId)) {
            const fixtureDetail = getCherylFixtureBatchDetail(batchId)
            if (fixtureDetail) return fixtureDetail
        }
        const params = new URLSearchParams()
        if (scope?.subjectId) params.set('subjectId', scope.subjectId)
        if (scope?.subjectType) params.set('subjectType', scope.subjectType)
        const query = params.toString()
        const res = await NodeAxios.get(
            `/knowledge-review/batches/${encodeURIComponent(batchId)}${query ? `?${query}` : ''}`,
            {
                skipErrorToast: true,
            }
        )
        return unwrapBody(res)
    },
    async processKnowledgeReviewBatch(
        batchId: string,
        command?: import('@/modules/interview-coach/knowledge-review-types').KnowledgeReviewCommand,
        scope?: { subjectId?: string; subjectType?: string }
    ): Promise<{ jobId: string; status: string; idempotentReplay: boolean }> {
        const params = new URLSearchParams()
        if (scope?.subjectId) params.set('subjectId', scope.subjectId)
        if (scope?.subjectType) params.set('subjectType', scope.subjectType)
        const query = params.toString()
        const res = await NodeAxios.post(
            `/knowledge-review/batches/${encodeURIComponent(batchId)}/process${query ? `?${query}` : ''}`,
            command ?? {},
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },
    async skipKnowledgeReviewBatch(
        batchId: string,
        command: import('@/modules/interview-coach/knowledge-review-types').KnowledgeReviewCommand,
        scope?: { subjectId?: string; subjectType?: string }
    ): Promise<{ dispositionId: string; status: 'skipped'; idempotentReplay: boolean }> {
        const params = new URLSearchParams()
        if (scope?.subjectId) params.set('subjectId', scope.subjectId)
        if (scope?.subjectType) params.set('subjectType', scope.subjectType)
        const query = params.toString()
        const res = await NodeAxios.post(
            `/knowledge-review/batches/${encodeURIComponent(batchId)}/skip${query ? `?${query}` : ''}`,
            command,
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },
    async cancelKnowledgeReviewBatch(
        batchId: string,
        scope?: { subjectId?: string; subjectType?: string }
    ): Promise<{ jobId: string; status: string; idempotentReplay: boolean }> {
        const params = new URLSearchParams()
        if (scope?.subjectId) params.set('subjectId', scope.subjectId)
        if (scope?.subjectType) params.set('subjectType', scope.subjectType)
        const query = params.toString()
        const res = await NodeAxios.post(
            `/knowledge-review/batches/${encodeURIComponent(batchId)}/cancel${query ? `?${query}` : ''}`,
            {},
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },

    async admitGoldenKnowledge(
        sessionId: string,
        idempotencyKey?: string
    ): Promise<{
        jobId: string
        state: {
            status: string
            canonicalRefs: string[]
            effects: Array<{ reviewItemId: string; family: string; status: string; reason?: string }>
        profileProjection?: {
            status: 'published' | 'blocked' | 'failed' | string
            snapshotId?: string
            snapshotRevision?: string
            coordinatorReceiptId?: string
            currentRead?: {
                runtimeArtifactId: string
                currentReadId: string
                currentReadRevision: string
                editionId?: string
                editionRevision?: string
            }
            foundation?: {
                runId: string
                targetSnapshotId?: string
                tfdSnapshotId?: string
                aloSnapshotId?: string
                unresolvedFields?: string[]
                changedFields?: string[]
                targetEnrichmentQueued?: boolean
            }
            reason?: string
        }
        }
        idempotentReplay: boolean
    }> {
        const key = idempotencyKey ?? `golden-admission:${sessionId}:${globalThis.crypto?.randomUUID?.() ?? Date.now()}`
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${encodeURIComponent(sessionId)}/post-interview-review/admit`,
            { idempotencyKey: key },
            { skipErrorToast: true, headers: { 'Idempotency-Key': key } }
        )
        return unwrapBody(res)
    },
    async dispatchKnowledgeReviewCommand(
        sessionId: string,
        command: import('@/modules/interview-coach/knowledge-review-types').KnowledgeReviewCommand
    ): Promise<{
        package: import('@/modules/interview-coach/meaning-compiler-types').PostInterviewKnowledgeReviewPackage
        idempotentReplay: boolean
        transitionReason?: string
    }> {
        if (isCherylKnowledgeReviewFixture() || isCherylFixtureBatchId(command.batchId))
            return dispatchCherylFixtureCommand(command)
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${encodeURIComponent(sessionId)}/post-interview-review/commands`,
            command,
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },

    async listHarvestProfiles(): Promise<HarvestProfileCandidate[]> {
        const now = Date.now()
        if (!harvestProfilesRequest || harvestProfilesRequest.expiresAt <= now) {
            const promise = NodeAxios.get('/live-interviews/harvest-profiles').then(res => {
                const body = unwrapBody<{ profiles: HarvestProfileCandidate[] }>(res)
                return body.profiles ?? []
            })
            harvestProfilesRequest = {
                promise,
                expiresAt: now + HARVEST_PROFILES_CACHE_TTL_MS,
            }
        }

        return harvestProfilesRequest.promise
    },

    async listSemanticHarvestProfiles(): Promise<{
        profiles: import('@/modules/interview-coach/dashboard-types').SemanticHarvestProfileView[]
        catalog: import('@/modules/interview-coach/dashboard-types').SemanticHarvestProfileView[]
    }> {
        const res = await NodeAxios.get('/live-interviews/semantic-harvest-profiles')
        return unwrapBody(res)
    },

    async getListeningContract(
        sessionId: string
    ): Promise<{ contract: import('@/modules/interview-coach/ListeningContractPanel').ListeningContractUi }> {
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}/listening-contract`)
        return unwrapBody(res)
    },

    async patchListeningContract(
        sessionId: string,
        body: {
            semanticHarvestProfileIds?: string[]
            processingPolicy?: {
                live: 'none' | 'organization'
                deepLive: 'disabled' | 'manual' | 'suggested' | 'automatic'
                postClose: 'none' | 'required'
            }
        }
    ): Promise<{ contract: import('@/modules/interview-coach/ListeningContractPanel').ListeningContractUi }> {
        const res = await NodeAxios.patch(`/live-interviews/sessions/${sessionId}/listening-contract`, body)
        return unwrapBody(res)
    },

    async activateHarvestProfile(
        sessionId: string,
        body: {
            profileId: string
            source?: 'interviewer' | 'post_close_recommendation'
            scope?: 'selected_episode' | 'from_activation' | 'full_transcript'
            activatedAtSequence?: number
            episodeId?: string
        }
    ): Promise<{ activation: unknown; assignedProfileIds: string[] }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/harvest/activate-profile`, body)
        return unwrapBody(res)
    },

    async getParticipantRoster(sessionId: string): Promise<{
        roster: import('@/modules/interview-coach/ParticipantRosterSetupSection').ParticipantRosterUi
        validation: { ok: boolean; issues: Array<{ code: string; message: string }> }
        suggestions: import('@/modules/interview-coach/ParticipantRosterSetupSection').ParticipantAssignmentUi[]
        audit: unknown[]
    }> {
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}/participant-assignments`)
        return unwrapBody(res)
    },

    async patchParticipantRoster(
        sessionId: string,
        body: {
            interviewFormat?: import('@/modules/interview-coach/ParticipantRosterSetupSection').InterviewFormatUi
            assignments?: import('@/modules/interview-coach/ParticipantRosterSetupSection').ParticipantAssignmentUi[]
            groupCollectiveBoundary?:
                | import('@/modules/interview-coach/ParticipantRosterSetupSection').GroupCollectiveBoundaryUi
                | null
            rosterConfirmed?: boolean
        }
    ): Promise<{
        roster: import('@/modules/interview-coach/ParticipantRosterSetupSection').ParticipantRosterUi
        validation: { ok: boolean; issues: Array<{ code: string; message: string }> }
    }> {
        const res = await NodeAxios.patch(`/live-interviews/sessions/${sessionId}/participant-assignments`, body)
        return unwrapBody(res)
    },

    async correctParticipantRole(
        sessionId: string,
        body: {
            participantId: string
            sessionRole: import('@/modules/interview-coach/ParticipantRosterSetupSection').SessionRoleUi
            organizationalRole?: string
            triggerReprocess?: boolean
        }
    ): Promise<{
        roster: import('@/modules/interview-coach/ParticipantRosterSetupSection').ParticipantRosterUi
        correction: { reprocessRequired: boolean; reprocessReason?: string }
    }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/participant-assignments/correct`, body)
        return unwrapBody(res)
    },

    async searchDirectedHarvest(
        sessionId: string,
        body: { query: string; structureHint?: string }
    ): Promise<{
        query: string
        hits: Array<{
            rangeId: string
            turnId: string
            sequence: number
            text: string
            speakerRole: string
            score: number
            episodeId?: string
        }>
        attributionStatus: string
    }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/harvest/directed/search`, body, {
            skipErrorToast: true,
        })
        return unwrapBody(res)
    },

    async compileDirectedHarvest(
        sessionId: string,
        body: {
            evidenceRangeIds: string[]
            profileId?: string
            destination?: string
        }
    ): Promise<{
        ok: boolean
        mode?: string
        objectIds?: string[]
        note?: string
        error?: string
    }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/harvest/directed/compile`, body, {
            skipErrorToast: true,
        })
        return unwrapBody(res)
    },

    /**
     * Deep-live checkpoint compile (Live Map). Returns 202 with queued run.
     * Backend may 404 when flag off / routes missing — callers degrade gracefully.
     */
    async compileDeepLive(
        sessionId: string,
        body: {
            idempotencyKey: string
            profileId?: string
            scope?: {
                kind:
                    | 'full_transcript_prefix'
                    | 'since_checkpoint'
                    | 'current_thread'
                    | 'story_candidate'
                    | 'evidence_ranges'
                priorRunId?: string
                threadId?: string
                candidateId?: string
                evidenceRangeIds?: string[]
            }
            transcriptThroughSequence?: number
        }
    ): Promise<{
        runId: string
        status: string
        snapshotId?: string
        transcriptThroughSequence?: number
        profileId?: string
        idempotentReplay?: boolean
        setupSoftWarn?: string
        error?: string
    }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/deep-live/compile`, body, {
            skipErrorToast: true,
        })
        return unwrapBody(res)
    },

    async getDeepLiveRun(
        sessionId: string,
        runId: string
    ): Promise<{
        run: {
            runId: string
            sessionId: string
            profileId: string
            status: string
            scope?: { kind: string; priorRunId?: string }
            transcriptThroughSequence?: number
            freshness?: string
            errorMessage?: string
            startedAt?: string
            completedAt?: string
            artifactId?: string
        }
        freshness: string
        currentMaxFinalizedSequence: number
        artifacts: Array<{
            artifactId?: string
            objects?: Array<{ objectId: string; kind?: string }>
        }>
    }> {
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}/deep-live/runs/${runId}`, {
            skipErrorToast: true,
        })
        return unwrapBody(res)
    },

    async cancelDeepLiveRun(sessionId: string, runId: string): Promise<{ runId: string; status: string }> {
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/deep-live/runs/${runId}/cancel`,
            {},
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },

    async getSession(
        sessionId: string
    ): Promise<LiveInterviewSessionSummary & { profileContext?: LiveInterviewProfileContext }> {
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}`)
        return unwrapBody(res)
    },

    async getTranscriptExport(sessionId: string): Promise<{
        sessionId: string
        title: string
        turnCount: number
        turns: Array<{
            sequence: number
            speakerRole: 'interviewee' | 'interviewer'
            speakerLabel: string
            text: string
        }>
        text: string
    }> {
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}/transcript/export`)
        return unwrapBody(res)
    },

    async ensureTranscriptFolder(sessionId: string): Promise<{
        folderId: string
        folderName: string
        parentFolderId: string
    }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/transcript/folder`)
        return unwrapBody(res)
    },

    async updateSession(
        sessionId: string,
        patch: {
            title?: string
            status?: string
            missionType?: string
            profileAttachment?: {
                subjectKind: 'person' | 'company' | 'product' | 'group'
                libraryEntityId: string
                subjectName?: string
                interviewTypeId: string
                foundationRunId?: string
                profileId?: string
                anchorStoryId?: string
                anchorRevision?: number
            }
            detachProfile?: boolean
            targetPrimitiveKeys?: string[]
            additionalHarvestTargets?: Array<{
                subjectKind: 'person' | 'company' | 'product' | 'group'
                libraryEntityId: string
                subjectName?: string
                foundationDisplayName?: string
                heftSubjectName?: string
                interviewTypeId?: string
                profileId?: string
                enabled?: boolean
            }>
            activeLiveGoalId?: string
            transcriptExport?: {
                documentId: string
                documentName: string
                exportedAt: string
            }
        }
    ): Promise<LiveInterviewSessionSummary & { profileContext?: LiveInterviewProfileContext }> {
        const res = await NodeAxios.patch(`/live-interviews/sessions/${sessionId}`, patch)
        return unwrapBody(res)
    },

    async createFoundationProfile(sessionId: string): Promise<{
        profileContext?: LiveInterviewProfileContext
        additionalHarvestTargets?: unknown[]
        backfillProcessedCount?: number
    }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/setup/create-foundation-profile`)
        return unwrapBody(res)
    },

    async linkFoundationProfile(
        sessionId: string,
        opts: { profileId?: string; foundationRunId?: string }
    ): Promise<{
        profileContext?: LiveInterviewProfileContext
        additionalHarvestTargets?: unknown[]
        backfillProcessedCount?: number
    }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/setup/link-foundation-profile`, opts)
        return unwrapBody(res)
    },

    async provisionHarvestSubject(
        sessionId: string,
        opts: { subjectKind: 'person' | 'company' | 'product' | 'group'; libraryEntityId: string }
    ): Promise<{
        profileId: string
        profileSubjectName: string
        heftSubjectName: string
        foundationRunId: string
        createdIdentityProfile: boolean
        createdHeftCuration: boolean
    }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/setup/provision-harvest-subject`, opts)
        return unwrapBody(res)
    },

    async deleteSession(sessionId: string, permanent = false): Promise<void> {
        const qs = permanent ? '?permanent=1' : ''
        await NodeAxios.delete(`/live-interviews/sessions/${sessionId}${qs}`)
    },

    async bulkSessionAction(
        sessionIds: string[],
        action: 'archive' | 'delete_permanent'
    ): Promise<{ processedCount: number; errors: Array<{ sessionId: string; error: string }> }> {
        const res = await NodeAxios.post('/live-interviews/sessions/bulk-action', {
            sessionIds,
            action,
        })
        return unwrapBody(res)
    },

    async resetSession(sessionId: string): Promise<LiveInterviewSessionSummary> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/reset`)
        return unwrapBody(res)
    },

    async createSession(
        title = 'CommZone live interview',
        opts?: {
            missionType?: string
            status?: string
            linkedInterviewSessionId?: string
            profileContext?: LiveInterviewProfileContext
            subjectContext?: {
                subjectKind: 'person' | 'company' | 'product' | 'group'
                libraryEntityId: string
            }
            profileAttachment?: {
                subjectKind: 'person' | 'company' | 'product' | 'group'
                libraryEntityId: string
                subjectName?: string
                interviewTypeId: string
                foundationRunId?: string
                profileId?: string
            }
        }
    ): Promise<LiveInterviewSession> {
        const res = await NodeAxios.post('/live-interviews/sessions', {
            title,
            missionType: opts?.missionType ?? 'founder_story',
            status: opts?.status,
            linkedInterviewSessionId: opts?.linkedInterviewSessionId,
            profileContext: opts?.profileContext,
            subjectContext: opts?.subjectContext,
            profileAttachment: opts?.profileAttachment,
        })
        const body = unwrapBody<Record<string, unknown>>(res)
        const sessionId = extractSessionId(body)
        return {
            sessionId,
            missionType: String(body.missionType ?? 'founder_story'),
            status: String(body.status ?? 'active'),
            sequence: typeof body.sequence === 'number' ? body.sequence : 0,
            panelRevision: typeof body.panelRevision === 'number' ? body.panelRevision : 0,
        }
    },

    async getPanel(sessionId: string): Promise<InterviewPanelSnapshot> {
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}/panel`)
        return unwrapBody<InterviewPanelSnapshot>(res)
    },

    async getDashboard(
        sessionId: string,
        opts?: { stage?: DashboardStage; stageSource?: 'manual' | 'inferred' }
    ): Promise<LiveInterviewDashboardSnapshot> {
        const params = new URLSearchParams()
        if (opts?.stage) params.set('stage', opts.stage)
        if (opts?.stageSource) params.set('stageSource', opts.stageSource)
        const qs = params.toString()
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}/dashboard${qs ? `?${qs}` : ''}`)
        return unwrapBody<LiveInterviewDashboardSnapshot>(res)
    },

    async postSteering(sessionId: string, signal: string, threadId?: string): Promise<{ ok: boolean; mode: string }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/steering`, { signal, threadId })
        return unwrapBody(res)
    },

    async postUtterance(
        sessionId: string,
        text: string
    ): Promise<{ panelRevision: number; turnId?: string; sequence?: number; eventId?: string }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/transcript-events`, {
            source: 'sim',
            sourceEventId: `commzone-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            speakerRole: 'interviewee',
            text,
            isFinal: true,
        })
        return unwrapBody(res)
    },

    async pasteTranscript(
        sessionId: string,
        body: { text: string; mode?: 'append' | 'replace'; previewOnly?: boolean }
    ): Promise<{
        pasteId: string
        mode: 'append' | 'replace'
        turnCount: number
        ingestedCount: number
        duplicateCount: number
        processingBlockedCount: number
        panelRevision: number
        sequence: number
        compileJob?: {
            runId: string
            status: string
            mode: string
            idempotentReplay?: boolean
        }
    }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/transcript-events/paste`, body)
        return unwrapBody(res)
    },

    async markMoveAsked(sessionId: string, moveId: string): Promise<void> {
        await NodeAxios.post(`/live-interviews/sessions/${sessionId}/interactions`, {
            type: 'mark_asked',
            moveId,
        })
    },

    async postInteraction(
        sessionId: string,
        body: {
            type: string
            objectId?: string
            moveId?: string
            signal?: string
            threadId?: string
            cleanedLabel?: string
            captureId?: string
            captureLabel?: string
            primitiveKeyHint?: string
        }
    ): Promise<{ panelRevision: number }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/interactions`, body)
        return unwrapBody(res)
    },

    async listDialogues(): Promise<DialogueSummary[]> {
        const res = await NodeAxios.get('/dev/live-interviews/sim/dialogues')
        const body = unwrapBody<{ dialogues: DialogueSummary[] }>(res)
        return body.dialogues ?? (body as unknown as DialogueSummary[])
    },

    async bootstrapOpening(
        sessionId: string,
        dialogueId: string
    ): Promise<{ linesPosted: number; debug: DebugSnapshot }> {
        const res = await NodeAxios.post(`/dev/live-interviews/sim/sessions/${sessionId}/bootstrap`, { dialogueId })
        return unwrapBody(res)
    },

    async conductorStep(
        sessionId: string,
        dialogueId: string,
        exchangeIndex: number,
        mode: 'engine' | 'scripted' | 'hybrid' = 'hybrid'
    ): Promise<ConductorStepResult> {
        const res = await NodeAxios.post(`/dev/live-interviews/sim/sessions/${sessionId}/conductor/step`, {
            dialogueId,
            exchangeIndex,
            mode,
        })
        return unwrapBody(res)
    },

    async conductorRun(
        sessionId: string,
        dialogueId: string,
        opts?: { mode?: 'engine' | 'scripted' | 'hybrid'; maxSteps?: number; bootstrap?: boolean }
    ): Promise<{ steps: ConductorStepResult[]; debug: DebugSnapshot }> {
        const res = await NodeAxios.post(`/dev/live-interviews/sim/sessions/${sessionId}/conductor/run`, {
            dialogueId,
            mode: opts?.mode ?? 'hybrid',
            maxSteps: opts?.maxSteps,
            bootstrap: opts?.bootstrap ?? false,
        })
        return unwrapBody(res)
    },

    async getDebugSnapshot(sessionId: string): Promise<DebugSnapshot> {
        const res = await NodeAxios.get(`/dev/live-interviews/sim/sessions/${sessionId}/debug`)
        return unwrapBody(res)
    },

    async bootstrapWorkbench(
        sessionId: string,
        opts?: { confirmAndApply?: boolean }
    ): Promise<{
        sessionId: string
        profileId?: string
        foundationRunId?: string
        steps: {
            foundationDestination: { ok: boolean; profileId?: string; foundationRunId?: string }
            onboardingResearch: { ok: boolean; fieldCount: number }
            foundationPlan: { ok: boolean; goalsAdded: number; skippedExisting: number }
            liveHandoff: { ok: boolean; goalCount?: number; error?: string }
            interviewBrief: { ok: boolean; source?: string; error?: string }
            keyPoints: { ok: boolean }
            ingest?: {
                reviewCardsConfirmed: number
                confirmErrors: string[]
                apply: { promotionsCompleted: number; errors: string[] }
            }
        }
        planView?: InterviewPlanView
    }> {
        const res = await NodeAxios.post(
            `/dev/live-interviews/sim/sessions/${sessionId}/bootstrap-workbench`,
            { confirmAndApply: opts?.confirmAndApply !== false },
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },

    async getInterviewPlan(sessionId: string): Promise<InterviewPlanView> {
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}/plan`)
        return unwrapBody(res)
    },

    async suggestLandscapeQuestion(
        sessionId: string,
        body: {
            headline: string
            summary?: string
            detail?: string
            contentBadge: string
            evidenceExcerpt?: string
        }
    ): Promise<{
        openingQuestion: string
        followUpQuestions: string[]
        rationale?: string
        meta: PlanAiMeta
    }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/plan/landscape/suggest-question`, body)
        return unwrapBody(res)
    },

    async patchInterviewPlan(
        sessionId: string,
        patch: { status?: string; consolidationComplete?: boolean }
    ): Promise<InterviewPlanView> {
        const res = await NodeAxios.patch(`/live-interviews/sessions/${sessionId}/plan`, patch)
        return unwrapBody(res)
    },

    async createInterviewGoal(
        sessionId: string,
        body: Partial<InterviewGoal>
    ): Promise<{ goal: InterviewGoal; plan: InterviewPlanView }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/goals`, body)
        return unwrapBody(res)
    },

    async patchInterviewGoal(
        sessionId: string,
        goalId: string,
        patch: Partial<InterviewGoal>
    ): Promise<{ goal: InterviewGoal; plan: InterviewPlanView }> {
        const res = await NodeAxios.patch(`/live-interviews/sessions/${sessionId}/goals/${goalId}`, patch)
        return unwrapBody(res)
    },

    async deleteInterviewGoal(sessionId: string, goalId: string): Promise<{ ok: boolean; plan: InterviewPlanView }> {
        const res = await NodeAxios.delete(`/live-interviews/sessions/${sessionId}/goals/${goalId}`)
        return unwrapBody(res)
    },

    async reorderInterviewGoals(
        sessionId: string,
        orderedIds: string[],
        stageAssignments?: Record<string, string>
    ): Promise<{ goals: InterviewGoal[]; plan: InterviewPlanView }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/goals/reorder`, {
            orderedIds,
            stageAssignments,
        })
        return unwrapBody(res)
    },

    async setInquiryLiveState(
        sessionId: string,
        inquiryId: string,
        liveState: 'live' | 'draft'
    ): Promise<{ plan: InterviewPlanView }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/inquiries/${inquiryId}/live-state`, {
            liveState,
        })
        return unwrapBody(res)
    },

    async setOpeningInquiry(sessionId: string, inquiryId: string | null): Promise<{ plan: InterviewPlanView }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/plan/opening`, {
            inquiryId,
        })
        return unwrapBody(res)
    },

    async addPlanOpportunity(
        sessionId: string,
        opportunityId: string
    ): Promise<{ inquiryId?: string; plan: InterviewPlanView }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/plan/opportunities/add`, {
            opportunityId,
        })
        return unwrapBody(res)
    },

    async promoteGoalFromKnowledge(
        sessionId: string,
        knowledgeId: string
    ): Promise<{ goal: InterviewGoal; plan: InterviewPlanView }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/goals/from-knowledge`, { knowledgeId })
        return unwrapBody(res)
    },

    async promoteGoalFromGap(
        sessionId: string,
        gapId: string
    ): Promise<{ goal: InterviewGoal; plan: InterviewPlanView }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/goals/from-gap`, { gapId })
        return unwrapBody(res)
    },

    async promoteGoalLiveReady(sessionId: string, goalId: string): Promise<{ plan: InterviewPlanView }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/goals/${goalId}/live-ready`)
        return unwrapBody(res)
    },

    async generateInterviewBrief(
        sessionId: string
    ): Promise<{ brief: InterviewBrief; markdown: string; meta?: PlanAiMeta }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/brief/generate`)
        return unwrapBody(res)
    },

    async generateLiveHandoff(
        sessionId: string
    ): Promise<{ liveHandoffPackage: LiveHandoffPackage; plan: InterviewPlanView }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/live-handoff/generate`)
        return unwrapBody(res)
    },

    async getLiveHandoff(sessionId: string): Promise<{
        liveHandoffPackage: LiveHandoffPackage | null
        activeLiveGoalId?: string
    }> {
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}/live-handoff`)
        return unwrapBody(res)
    },

    async patchLiveGoalStatus(
        sessionId: string,
        goalId: string,
        status: import('@/modules/interview-coach/plan-types').LiveGoalStatus
    ): Promise<{ liveHandoffPackage: LiveHandoffPackage | null; activeLiveGoalId?: string }> {
        const res = await NodeAxios.patch(`/live-interviews/sessions/${sessionId}/live-handoff/goals/${goalId}`, {
            status,
        })
        return unwrapBody(res)
    },

    async createAgendaShare(sessionId: string): Promise<{
        token: string
        path: string
        url: string
        agendaSharePath?: string
    }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/agenda-share`)
        return unwrapBody(res)
    },

    async revokeAgendaShare(sessionId: string): Promise<{ revoked: boolean }> {
        const res = await NodeAxios.delete(`/live-interviews/sessions/${sessionId}/agenda-share`)
        return unwrapBody(res)
    },

    async createSessionRelationship(
        sessionId: string,
        body: {
            fromObjectId?: string
            toObjectId?: string
            fromNodeId?: string
            toNodeId?: string
            type: string
            direction?: 'out' | 'in'
        }
    ): Promise<{
        panelRevision: number
        graph: LiveInterviewDashboardSnapshot['graph']
    }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/relationships`, body)
        return unwrapBody(res)
    },

    async patchMapCuration(
        sessionId: string,
        body: {
            hideNodeId?: string
            unhideNodeId?: string
            nodeId?: string
            note?: string
            preferredMapPatternId?: string | null
            patternManuallyLocked?: boolean
            pinnedNodePlacement?: { nodeId: string; position: { x: number; y: number } }
            clearPinnedPlacements?: boolean
        }
    ): Promise<{
        panelRevision: number
        mapCuration?: LiveInterviewDashboardSnapshot['session']['mapCuration']
        graph: LiveInterviewDashboardSnapshot['graph']
    }> {
        const res = await NodeAxios.patch(`/live-interviews/sessions/${sessionId}/map-curation`, body)
        return unwrapBody(res)
    },

    async stageMapProbe(
        sessionId: string,
        body: {
            question: string
            obligationId?: string
            sourceStoryRef?: string
            sourceTermRef?: string
            missingSemanticRole?: string
            sourceNodeId?: string
        }
    ): Promise<{ stagedMapProbe: LiveInterviewDashboardSnapshot['stagedMapProbe'] }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/map-probe/stage`, body)
        return unwrapBody(res)
    },

    async dismissStagedMapProbe(sessionId: string): Promise<{ ok: boolean }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/map-probe/dismiss`)
        return unwrapBody(res)
    },

    async generateStoryVisual(
        sessionId: string,
        body: {
            sourceStoryRef: string
            sourceStoryNodeId: string
            storyRevision?: number
            storyText?: string
            manualSensitiveApproval?: boolean
            regenerate?: boolean
        }
    ): Promise<{ ok: boolean; adornmentId?: string; status?: string; reason?: string }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/story-visuals/generate`, body)
        return unwrapBody(res)
    },

    async patchStoryVisualSettings(
        sessionId: string,
        body: Partial<NonNullable<LiveInterviewDashboardSnapshot['session']['mapCuration']>['storyVisuals']>
    ): Promise<{ mapCuration?: LiveInterviewDashboardSnapshot['session']['mapCuration'] }> {
        const res = await NodeAxios.patch(`/live-interviews/sessions/${sessionId}/story-visuals/settings`, body)
        return unwrapBody(res)
    },

    async getStoryVisualAdornment(
        sessionId: string,
        adornmentId: string
    ): Promise<{ adornment: import('@/modules/interview-coach/dashboard-types').StoryVisualAdornment }> {
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}/story-visuals/${adornmentId}`)
        return unwrapBody(res)
    },

    async promoteStoryVisual(
        sessionId: string,
        adornmentId: string
    ): Promise<{ ok: boolean; mediaDocumentId?: string; reason?: string }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/story-visuals/${adornmentId}/promote`)
        return unwrapBody(res)
    },

    async createMapShare(sessionId: string): Promise<{
        token: string
        path: string
        url: string
        mapSharePath?: string
    }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/map-share`)
        return unwrapBody(res)
    },

    async revokeMapShare(sessionId: string): Promise<{ revoked: boolean }> {
        const res = await NodeAxios.delete(`/live-interviews/sessions/${sessionId}/map-share`)
        return unwrapBody(res)
    },

    async createLandscapeShare(sessionId: string): Promise<{
        token: string
        path: string
        url: string
        landscapeSharePath?: string
    }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/landscape-share`)
        return unwrapBody(res)
    },

    async revokeLandscapeShare(sessionId: string): Promise<{ revoked: boolean }> {
        const res = await NodeAxios.delete(`/live-interviews/sessions/${sessionId}/landscape-share`)
        return unwrapBody(res)
    },

    async aiSuggestGoals(
        sessionId: string,
        max = 5
    ): Promise<{ suggestions: PlanAiSuggestedGoalDraft[]; overallRationale?: string; meta: PlanAiMeta }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/ai/suggest-goals`, { max })
        return unwrapBody(res)
    },

    async aiApplySuggestGoals(
        sessionId: string,
        max = 5
    ): Promise<{ goals: InterviewGoal[]; plan: InterviewPlanView; meta: PlanAiMeta }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/ai/suggest-goals/apply`, { max })
        return unwrapBody(res)
    },

    async aiEthnographicQuestions(
        sessionId: string,
        goalId: string
    ): Promise<{ questions: PlanAiEthnographicQuestion[]; meta: PlanAiMeta }> {
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/ai/goals/${goalId}/ethnographic-questions`
        )
        return unwrapBody(res)
    },

    async aiApplyEthnographic(
        sessionId: string,
        goalId: string
    ): Promise<{ goal: InterviewGoal; plan: InterviewPlanView; meta: PlanAiMeta }> {
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/ai/goals/${goalId}/ethnographic-questions/apply`
        )
        return unwrapBody(res)
    },

    async aiFollowUps(
        sessionId: string,
        goalId: string,
        kind?: string
    ): Promise<{ kind: string; followUps: PlanAiFollowUp[]; meta: PlanAiMeta }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/ai/goals/${goalId}/follow-ups`, {
            kind,
        })
        return unwrapBody(res)
    },

    async aiApplyFollowUps(
        sessionId: string,
        goalId: string,
        kind?: string
    ): Promise<{ goal: InterviewGoal; plan: InterviewPlanView; meta: PlanAiMeta }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/ai/goals/${goalId}/follow-ups/apply`, {
            kind,
        })
        return unwrapBody(res)
    },

    async aiLaddering(sessionId: string, goalId: string): Promise<{ rungs: PlanAiLadderingRung[]; meta: PlanAiMeta }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/ai/goals/${goalId}/laddering-prompts`)
        return unwrapBody(res)
    },

    async aiApplyLaddering(
        sessionId: string,
        goalId: string
    ): Promise<{ goal: InterviewGoal; plan: InterviewPlanView; meta: PlanAiMeta }> {
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/ai/goals/${goalId}/laddering-prompts/apply`
        )
        return unwrapBody(res)
    },

    async aiFindProof(sessionId: string, goalId: string): Promise<{ proofGaps: PlanAiProofGap[]; meta: PlanAiMeta }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/ai/goals/${goalId}/find-proof`)
        return unwrapBody(res)
    },

    async aiApplyFindProof(
        sessionId: string,
        goalId: string
    ): Promise<{ goal: InterviewGoal; plan: InterviewPlanView; meta: PlanAiMeta }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/ai/goals/${goalId}/find-proof/apply`)
        return unwrapBody(res)
    },

    async aiFets(sessionId: string, goalId: string): Promise<{ prompts: PlanAiFetsPrompt[]; meta: PlanAiMeta }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/ai/goals/${goalId}/convert-to-fets`)
        return unwrapBody(res)
    },

    async aiApplyFets(
        sessionId: string,
        goalId: string
    ): Promise<{ goal: InterviewGoal; plan: InterviewPlanView; meta: PlanAiMeta }> {
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/ai/goals/${goalId}/convert-to-fets/apply`
        )
        return unwrapBody(res)
    },

    async aiSummarizeBrief(sessionId: string): Promise<{ brief: Partial<InterviewBrief>; meta: PlanAiMeta }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/ai/summarize-into-brief`)
        return unwrapBody(res)
    },

    async listCaptureCandidates(sessionId: string): Promise<{ captureCandidates: CaptureCandidate[] }> {
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}/capture-candidates`)
        return unwrapBody(res)
    },

    async createCaptureCandidate(
        sessionId: string,
        body: { text: string; captureType?: string; relatedGoalIds?: string[]; linkedTurnId?: string }
    ): Promise<{ captureCandidate: CaptureCandidate; all: CaptureCandidate[] }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/capture-candidates`, body)
        return unwrapBody(res)
    },

    async patchCaptureCandidate(
        sessionId: string,
        candidateId: string,
        patch: { trustState?: string; text?: string; captureType?: string; relatedGoalIds?: string[] }
    ): Promise<{ captureCandidates: CaptureCandidate[] }> {
        const res = await NodeAxios.patch(
            `/live-interviews/sessions/${sessionId}/capture-candidates/${candidateId}`,
            patch
        )
        return unwrapBody(res)
    },

    async deleteCaptureCandidate(
        sessionId: string,
        candidateId: string
    ): Promise<{ captureCandidates: CaptureCandidate[] }> {
        const res = await NodeAxios.delete(`/live-interviews/sessions/${sessionId}/capture-candidates/${candidateId}`)
        return unwrapBody(res)
    },

    async getReviewQueue(sessionId: string): Promise<ReviewQueueView> {
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}/review`)
        return unwrapBody(res)
    },

    async promoteCaptureCandidate(
        sessionId: string,
        candidateId: string,
        approve: boolean,
        cleanedText?: string
    ): Promise<{ captureCandidates: CaptureCandidate[] }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/review/${candidateId}/promote`, {
            approve,
            cleanedText,
        })
        return unwrapBody(res)
    },

    async digestHarvestCapture(
        sessionId: string,
        body: {
            transcriptText: string
            categoryId: string
            slotLabel: string
            existingDraft?: string
            subjectName?: string
        }
    ): Promise<{
        summary: string
        source: 'llm' | 'heuristic'
        categoryId: string
        warning?: string
        candidates?: string[]
        title?: string
        titleShort?: string
        titleMedium?: string
        description?: string
    }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/harvest/digest`, body)
        return unwrapBody(res)
    },

    async generateKeyPoints(sessionId: string): Promise<{ keyPointsPage: KeyPointsPage }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/key-points/generate`)
        return unwrapBody(res)
    },

    async getKeyPoints(sessionId: string): Promise<{ keyPointsPage: KeyPointsPage | null }> {
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}/key-points`)
        return unwrapBody(res)
    },

    async patchOnboardingFields(
        sessionId: string,
        fields: Array<{ fieldKey: string; value: string; label?: string }>
    ): Promise<{ onboardingResearch: OnboardingResearchSeed; plan: InterviewPlanView }> {
        const res = await NodeAxios.patch(`/live-interviews/sessions/${sessionId}/onboarding/fields`, { fields })
        return unwrapBody(res)
    },

    async seedOnboardingResearch(
        sessionId: string,
        opts?: { websiteUrl?: string; force?: boolean }
    ): Promise<{ onboardingResearch: OnboardingResearchSeed; plan: InterviewPlanView }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/onboarding/seed`, opts ?? {})
        return unwrapBody(res)
    },

    async loadFoundationProfilePlan(sessionId: string): Promise<{
        planId: string
        outcomeId: string
        goalsAdded: number
        skippedExisting: number
        goals: InterviewGoal[]
        plan: InterviewPlanView
    }> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/plan/load-foundation-template`)
        return unwrapBody(res)
    },

    async getMeaningCompilerObjects(
        sessionId: string
    ): Promise<import('@/modules/interview-coach/meaning-compiler-types').MeaningCompilerObjectsResponse> {
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}/meaning-compiler/objects`, {
            skipErrorToast: true,
        })
        return unwrapBody(res)
    },

    async getSemanticReview(
        sessionId: string,
        filters?: {
            status?: string
            severity?: string
            reviewerTarget?: string
            issueKind?: string
        }
    ): Promise<import('@/modules/interview-coach/meaning-compiler-types').SemanticReviewProjection> {
        const params = new URLSearchParams()
        for (const [key, value] of Object.entries(filters ?? {})) {
            if (value) params.set(key, value)
        }
        const query = params.toString()
        const res = await NodeAxios.get(
            `/live-interviews/sessions/${sessionId}/semantic-review${query ? `?${query}` : ''}`,
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },
    async getPostInterviewKnowledgeReview(
        sessionId: string,
        options?: { cleanRoom?: boolean }
    ): Promise<import('@/modules/interview-coach/meaning-compiler-types').PostInterviewKnowledgeReviewPackage> {
        const query = options?.cleanRoom ? '?cleanRoom=true' : ''
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}/post-interview-review${query}`, {
            skipErrorToast: true,
        })
        return unwrapBody(res)
    },
    async decidePostInterviewKnowledgeReview(
        sessionId: string,
        reviewItemId: string,
        body: {
            decision: import('@/modules/interview-coach/meaning-compiler-types').PostInterviewKnowledgeReviewDecision
            commandId?: string
            expectedPackageRevision?: number
            expectedReviewItemRevision?: string
            correctionIntent?: 'misunderstood_meaning' | 'misleading_wording' | 'new_information'
            issuedAt?: string
            correctedRepresentation?: string
            reason?: string
        }
    ): Promise<import('@/modules/interview-coach/meaning-compiler-types').PostInterviewKnowledgeReviewPackage> {
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/post-interview-review/items/${reviewItemId}/decision`,
            body,
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },
    async batchAcceptPostInterviewKnowledgeReview(
        sessionId: string,
        reviewItemIds: string[]
    ): Promise<import('@/modules/interview-coach/meaning-compiler-types').PostInterviewKnowledgeReviewPackage> {
        if (isCherylKnowledgeReviewFixture() || isCherylFixtureReviewItems(reviewItemIds)) {
            return dispatchCherylFixtureCommand({
                schemaVersion: 'knowledge-review-command-v1',
                commandId: globalThis.crypto?.randomUUID?.() ?? `fixture-batch-accept-${Date.now()}`,
                batchId: getCherylFixtureBatchId(),
                type: 'accept_ready_review_items',
                reviewItemIds,
            }).package
        }
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/post-interview-review/batch-accept`,
            { reviewItemIds },
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },
    async getPostInterviewProfileHandoffGate(
        sessionId: string
    ): Promise<{ eligible: boolean; reasonCodes: string[]; candidateIds: string[] }> {
        const res = await NodeAxios.get(
            `/live-interviews/sessions/${sessionId}/post-interview-review/profile-handoff`,
            {
                skipErrorToast: true,
            }
        )
        return unwrapBody(res)
    },
    async getSemanticReviewIssue(
        sessionId: string,
        reviewIssueId: string
    ): Promise<import('@/modules/interview-coach/meaning-compiler-types').SemanticReviewItem> {
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}/semantic-review/${reviewIssueId}`, {
            skipErrorToast: true,
        })
        return unwrapBody(res)
    },
    async resolveSemanticReviewIssue(
        sessionId: string,
        reviewIssueId: string,
        body: {
            action: string
            authority: string
            choice?: string
            freeText?: string
            rationale?: string
            requestRecompile?: boolean
        }
    ): Promise<{
        issue: import('@/modules/interview-coach/meaning-compiler-types').SemanticReviewItem['issue']
        resolution: unknown
        clarificationEvidence?: unknown
        recompileReceipt: unknown
        projection: import('@/modules/interview-coach/meaning-compiler-types').SemanticReviewProjection
    }> {
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/semantic-review/${reviewIssueId}/resolutions`,
            body,
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },
    async postAnalyticalReadDisposition(
        sessionId: string,
        body: { readId: string; disposition: 'kept' | 'dismissed' }
    ): Promise<
        import('@/modules/interview-coach/meaning-compiler-types').MeaningCompilerObjectsResponse & {
            readId: string
            workflowStatus: 'kept' | 'dismissed'
        }
    > {
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/meaning-compiler/analytical-disposition`,
            body,
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },

    async reprocessMeaningCompiler(
        sessionId: string
    ): Promise<import('@/modules/interview-coach/meaning-compiler-types').MeaningCompilerObjectsResponse> {
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/meaning-compiler/reprocess`,
            {},
            {
                skipErrorToast: true,
            }
        )
        return unwrapBody(res)
    },

    async getActiveMeaningCompilerCloseRun(sessionId: string): Promise<{
        active: {
            runId: string
            status: string
            trigger?: string
            objectsCreated?: number | null
            errorMessage?: string | null
            publishStatus?: string | null
            enrichmentStatus?: string | null
            compilerReadiness?: string | null
            heftReadiness?: string | null
        } | null
        latest?: {
            runId: string
            status: string
            trigger?: string
            objectsCreated?: number | null
            errorMessage?: string | null
            publishStatus?: string | null
            enrichmentStatus?: string | null
            compilerReadiness?: string | null
            heftReadiness?: string | null
        } | null
    }> {
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}/meaning-compiler/close/active`, {
            skipErrorToast: true,
        })
        return unwrapBody(res)
    },

    async cancelActiveMeaningCompilerCloseRun(sessionId: string): Promise<{ runId: string; status: string }> {
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/meaning-compiler/close/cancel-active`,
            {},
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },

    async confirmMeaningCompilerObject(
        sessionId: string,
        body: {
            objectId: string
            expectedObjectVersion: number
            decision: 'confirm' | 'reject'
            objectType: string
            canonicalStatement?: string
            semanticLossAcknowledged?: boolean
            structureComponents?: Array<{ componentId: string; confirmationScope?: string; value?: string }>
        }
    ): Promise<import('@/modules/interview-coach/meaning-compiler-types').MeaningCompilerConfirmResponse> {
        let assertions: Array<Record<string, unknown>>

        if (body.structureComponents && body.structureComponents.length > 0) {
            assertions = body.structureComponents
                .filter(comp => comp.confirmationScope)
                .map(comp => {
                    const assertion: Record<string, unknown> = {
                        assertionId: `${body.objectId}_${body.expectedObjectVersion}_${comp.componentId}`,
                        scope: comp.confirmationScope!,
                        decision: body.decision,
                    }
                    if (body.decision === 'confirm' && body.canonicalStatement?.trim()) {
                        assertion.replacementText = body.canonicalStatement.trim()
                    }
                    return assertion
                })

            if (assertions.length === 0) {
                const primaryComponent = body.structureComponents[0]
                assertions = [
                    {
                        assertionId: `${body.objectId}_${body.expectedObjectVersion}`,
                        scope: primaryComponent.confirmationScope ?? 'event_accuracy',
                        decision: body.decision,
                        ...(body.decision === 'confirm' && body.canonicalStatement?.trim()
                            ? { replacementText: body.canonicalStatement.trim() }
                            : {}),
                    },
                ]
            }
        } else {
            const scope =
                body.objectType === 'IdentityFact'
                    ? 'identity_interpretation'
                    : body.objectType === 'Story'
                      ? 'event_accuracy'
                      : 'event_accuracy'
            assertions = [
                {
                    assertionId: `${body.objectId}_${body.expectedObjectVersion}`,
                    scope,
                    decision: body.decision,
                    ...(body.decision === 'confirm' && body.canonicalStatement?.trim()
                        ? { replacementText: body.canonicalStatement.trim() }
                        : {}),
                },
            ]
        }

        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/meaning-compiler/confirm`, {
            objectId: body.objectId,
            expectedObjectVersion: body.expectedObjectVersion,
            actor: { actorId: 'live_review_ui', role: 'operator' },
            assertions,
            ...(body.semanticLossAcknowledged ? { semanticLossAcknowledged: true } : {}),
        })
        return unwrapBody(res)
    },

    async resolveMeaningCompilerConflict(
        sessionId: string,
        body: {
            conflictId: string
            resolution: 'accept_deterministic' | 'accept_llm' | 'both_acceptable' | 'neither_correct'
            notes?: string
        }
    ): Promise<{ resolved: { conflictId: string; resolution: string } }> {
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/meaning-compiler/resolve-conflict`,
            body,
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },

    async restoreMeaningCompilerSuggestion(
        sessionId: string,
        body: {
            objectId?: string
            objectIds?: string[]
            objectVersion?: number
            objectVersions?: Record<string, number>
        }
    ): Promise<
        import('@/modules/interview-coach/meaning-compiler-types').MeaningCompilerObjectsResponse & {
            ok?: string[]
            failed?: Array<{ objectId: string; reason: string }>
        }
    > {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/meaning-compiler/restore`, body, {
            skipErrorToast: true,
        })
        return unwrapBody(res)
    },

    async clarifyMeaningCompilerObject(
        sessionId: string,
        body: {
            objectId: string
            expectedObjectVersion: number
            userExplanation: string
            intent: 'what_is_this' | 'wrong_label' | 'wrong_slot' | 'missing_context'
            currentStatement?: string
            objectType?: string
            destinationPrimitiveKeys?: string[]
            evidenceExcerpt?: string
            preview?: boolean
            apply?: boolean
            triggerReprocess?: boolean
        }
    ): Promise<
        import('@/modules/interview-coach/meaning-compiler-types').MeaningCompilerObjectsResponse & {
            suggestion?: {
                summary: string
                objectType: string
                destinationPrimitiveKeys: string[]
                canonicalStatement: string
                destinationSlotLabel: string
                relationshipHint?: string
                taxonomyCode: string
                entityKind?: 'person' | 'company' | 'product' | 'group'
                followUpActions?: Array<{
                    type: 'identity_entity_decision'
                    handoffTab: 'identity'
                    candidateId: string
                    entityKind: 'person' | 'company' | 'product' | 'group'
                    label: string
                    relationshipHint?: string
                    reason: string
                    questionDisplay: string
                }>
                confidence: 'rule' | 'llm'
            }
            clarification?: unknown
            compileJob?: unknown
        }
    > {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/meaning-compiler/clarify`, body, {
            skipErrorToast: true,
        })
        return unwrapBody(res)
    },

    async reclassifyMeaningCompilerCandidate(
        sessionId: string,
        body: { candidateId: string; toKind: string; actorId?: string }
    ): Promise<
        import('@/modules/interview-coach/meaning-compiler-types').MeaningCompilerObjectsResponse & {
            assessments?: unknown[]
        }
    > {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/meaning-compiler/reclassify`, body, {
            skipErrorToast: true,
        })
        return unwrapBody(res)
    },

    async undoMeaningCompilerBulk(sessionId: string): Promise<
        import('@/modules/interview-coach/meaning-compiler-types').MeaningCompilerObjectsResponse & {
            undone?: boolean
        }
    > {
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/meaning-compiler/undo-bulk`,
            {},
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },

    async bulkMeaningCompilerDisposition(
        sessionId: string,
        body: {
            action: 'bulk_reject' | 'bulk_restore' | 'bulk_keep_selected'
            objectIds: string[]
            objectVersions: Record<string, number>
            universeObjectIds?: string[]
        }
    ): Promise<
        import('@/modules/interview-coach/meaning-compiler-types').MeaningCompilerObjectsResponse & {
            ok?: string[]
            failed?: Array<{ objectId: string; reason: string }>
            canUndo?: boolean
        }
    > {
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/meaning-compiler/bulk-disposition`,
            body,
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },

    async getSessionFill(
        sessionId: string
    ): Promise<import('@/modules/interview-coach/session-fill-types').SessionFillView> {
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}/session-fill`, {
            skipErrorToast: true,
        })
        return unwrapBody(res)
    },

    async queryProfileFillProjection(sessionId: string): Promise<{
        status: string
        projection?: {
            data?: {
                sessionFillView?: import('@/modules/interview-coach/session-fill-types').SessionFillView
                domainStripCounts?: Record<string, unknown>
            }
        }
    }> {
        const res = await NodeAxios.post(
            '/meaning/profile-fill-projections/query',
            {
                scope: { kind: 'session', sessionId },
                destinationFamilies: ['heft', 'foundation', 'identity'],
                include: ['session_fill_view'],
            },
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },

    /** G3 — Plane 4 profile-fill decision (no frontend wallet patches). */
    async executeProfileFillCommand(command: ProfileFillCommandInput): Promise<{
        ok: boolean
        dispositionId: string
        canonicalRecordId: string
        newRevision?: string
        memoryLedgerReceiptId?: string
        walletEntry?: {
            walletItemId: string
            applyState: 'staged' | 'applying' | 'applied' | 'apply_failed' | 'superseded'
        }
        idempotentReplay?: boolean
        error?: string
    }> {
        const res = await NodeAxios.post('/meaning/profile-fill/commands', command, {
            skipErrorToast: true,
        })
        return unwrapBody(res)
    },

    async executeFoundationReviewCommand(command: {
        sessionId: string
        commandId: string
        targetId: string
        targetType: 'candidate' | 'obligation'
        action:
            | 'keep'
            | 'edit'
            | 'reject'
            | 'defer'
            | 'ask'
            | 'add_to_plan'
            | 'mark_not_applicable'
            | 'set_tfd_scope'
            | 'stage_for_profile'
        expectedReviewRevision: string
        payload?: Record<string, unknown>
    }): Promise<{
        ok: boolean
        commandId?: string
        sessionRevision?: number
        reviewRevision?: string
        disposition?: Record<string, unknown>
        rowPatch?: { targetId: string; action: string }
        countPatch?: Record<string, number>
        error?: string
    }> {
        const res = await NodeAxios.post('/meaning/foundation-review/commands', command, {
            skipErrorToast: true,
        })
        return unwrapBody(res)
    },

    async queryStrategicTerrain(params: {
        sessionId: string
        subjectRef: string
        goalRef?: string
        requestedAudience?: 'interviewer' | 'guest' | 'subject'
    }): Promise<{
        status?: 'ready' | 'accepted'
        cacheHit?: boolean
        opportunityList?: Array<{
            opportunityId: string
            opportunityKey: string
            conceptRef: string
            reason: string
            priority: 'now' | 'next' | 'later'
            recommendedAction: string
            explanation: string
            ranking: { score: number; goalRelevance: number }
        }>
        projection?: {
            projectionId?: string
            projectionRevision?: string
            effectiveAudience?: string
            data?: {
                opportunities?: Array<{
                    opportunityId: string
                    opportunityKey: string
                    conceptRef: string
                    reason: string
                    priority: 'now' | 'next' | 'later'
                    recommendedAction: string
                    explanation: string
                    ranking: { score: number; goalRelevance: number }
                }>
            }
        }
    }> {
        const res = await NodeAxios.post(
            '/meaning/strategic-terrain/query',
            {
                scope: { kind: 'session', sessionId: params.sessionId },
                subjectRef: params.subjectRef,
                lens: {
                    mode: params.goalRef ? 'goal_directed' : 'open_exploration',
                    goalRef: params.goalRef,
                    domainRootRef: 'capability:marketing',
                },
                bounds: { maximumRegions: 16, maximumOpportunities: 8 },
                requestedAudience: params.requestedAudience,
            },
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },

    async composeStrategicTerrainMap(params: {
        sourceProjectionId: string
        patternKey?: 'strategic_terrain_v1' | 'live_interview_terrain_v1' | 'subject_landscape_v1'
        viewport?: 'desktop' | 'mobile' | 'guest'
    }): Promise<{
        mapProjection?: {
            patternKey: string
            nodes: Array<{ nodeId: string; label: string; state?: string }>
        }
        effectiveAudience?: string
    }> {
        const res = await NodeAxios.post(
            '/meaning/map-projections/compose',
            {
                sourceProjectionId: params.sourceProjectionId,
                patternKey: params.patternKey ?? 'strategic_terrain_v1',
                viewport: params.viewport ?? 'desktop',
            },
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },

    async postStrategicOpportunityAction(
        opportunityId: string,
        body: {
            projectionId: string
            projectionRevision: string
            opportunityKey: string
            idempotencyKey: string
            subjectRef: string
            sessionId: string
            action: 'ask_now' | 'add_to_plan' | 'dismiss'
            placement?: 'next' | 'later' | 'backlog'
            questionSeed?: string
            dispositionScope?: 'projection' | 'lens' | 'subject'
        }
    ): Promise<{ receipt?: { receiptId: string } }> {
        const payload =
            body.action === 'dismiss'
                ? {
                      ...body,
                      action: 'dismiss' as const,
                      dispositionScope: body.dispositionScope ?? 'subject',
                  }
                : body.action === 'add_to_plan'
                  ? {
                        ...body,
                        action: 'add_to_plan' as const,
                        placement: body.placement ?? 'next',
                    }
                  : {
                        ...body,
                        action: 'ask_now' as const,
                        questionSeed: body.questionSeed,
                    }
        const res = await NodeAxios.post(
            `/meaning/strategic-opportunities/${encodeURIComponent(opportunityId)}/actions`,
            payload,
            { skipErrorToast: true }
        )
        return unwrapBody(res)
    },

    async getSessionFillApplyPreview(
        sessionId: string,
        layer?: import('@/modules/interview-coach/session-fill-types').SessionFillApplyLayer
    ): Promise<import('@/modules/interview-coach/session-fill-types').ApplySessionPromotionsPreview> {
        const res = await NodeAxios.get(`/live-interviews/sessions/${sessionId}/session-fill/apply-preview`, {
            skipErrorToast: true,
            params: layer && layer !== 'full' ? { layer } : undefined,
        })
        return unwrapBody(res)
    },

    async applySessionFill(
        sessionId: string,
        layer?: import('@/modules/interview-coach/session-fill-types').SessionFillApplyLayer
    ): Promise<import('@/modules/interview-coach/session-fill-types').ApplySessionFillResponse> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/session-fill/apply`, undefined, {
            params: layer && layer !== 'full' ? { layer } : undefined,
        })
        return unwrapBody(res)
    },

    async patchSessionFillSlot(
        sessionId: string,
        primitiveKey: string,
        body: { value?: string | string[]; action?: 'clear' }
    ): Promise<import('@/modules/interview-coach/session-fill-types').PatchSessionFillSlotResponse> {
        const res = await NodeAxios.patch(
            `/live-interviews/sessions/${sessionId}/session-fill/slots/${encodeURIComponent(primitiveKey)}`,
            body
        )
        return unwrapBody(res)
    },

    async patchSessionFillAudienceProfile(
        sessionId: string,
        profileKey: string,
        body: import('@/modules/interview-coach/session-fill-types').InterviewAudienceProfilePatch
    ): Promise<import('@/modules/interview-coach/session-fill-types').PatchSessionFillAudienceProfileResponse> {
        const res = await NodeAxios.patch(
            `/live-interviews/sessions/${sessionId}/session-fill/audience-profiles/${encodeURIComponent(profileKey)}`,
            body
        )
        return unwrapBody(res)
    },

    async dismissSessionFillAudienceDeliveryRow(
        sessionId: string,
        body: { rowKey: string; profileKey?: string }
    ): Promise<{ sessionFill: import('@/modules/interview-coach/session-fill-types').SessionFillView }> {
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/session-fill/audience-delivery-rows/dismiss`,
            body
        )
        return unwrapBody(res)
    },

    async patchSessionFillAloDeliveryRow(
        sessionId: string,
        rowKey: string,
        body: {
            rowKey?: string
            canonicalRecordId?: string
            statement: string
            aloKind: 'asset' | 'liability' | 'omission'
            subjectRef: string
            groupKey: string
            groupKind: 'person' | 'company' | 'product' | 'group'
            groupTitle: string
        }
    ): Promise<{ sessionFill: import('@/modules/interview-coach/session-fill-types').SessionFillView }> {
        const res = await NodeAxios.patch(
            `/live-interviews/sessions/${sessionId}/session-fill/alo-delivery-rows/${encodeURIComponent(rowKey)}`,
            body
        )
        return unwrapBody(res)
    },

    async patchSessionFillIdentityDimensionDeliveryRow(
        sessionId: string,
        rowKey: string,
        body: {
            rowKey?: string
            canonicalRecordId?: string
            label: string
            reflectionStatement: string
            axis: string
            subjectRef?: string
        }
    ): Promise<
        | { sessionFill: import('@/modules/interview-coach/session-fill-types').SessionFillView }
        | { accepted: true; rowKey: string; walletApplyState: 'staged' }
    > {
        const res = await NodeAxios.patch(
            `/live-interviews/sessions/${sessionId}/session-fill/identity-dimension-delivery-rows/${encodeURIComponent(rowKey)}`,
            body
        )
        return unwrapBody(res)
    },

    async dismissSessionFillAloDeliveryRow(
        sessionId: string,
        body: { rowKey: string }
    ): Promise<{ sessionFill: import('@/modules/interview-coach/session-fill-types').SessionFillView }> {
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/session-fill/alo-delivery-rows/dismiss`,
            body
        )
        return unwrapBody(res)
    },

    async dismissSessionFillContribution(
        sessionId: string,
        objectId: string
    ): Promise<import('@/modules/interview-coach/session-fill-types').DismissSessionFillContributionResponse> {
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/session-fill/contributions/${encodeURIComponent(objectId)}/dismiss`
        )
        return unwrapBody(res)
    },

    async postIdentityEntityDecision(
        sessionId: string,
        body: {
            candidateId: string
            entityKind: 'person' | 'company' | 'product' | 'group'
            label: string
            decision: 'harvest' | 'reference_only'
            createProfile?: boolean
        }
    ): Promise<import('@/modules/interview-coach/session-fill-types').IdentityEntityDecisionResponse> {
        const res = await NodeAxios.post(`/live-interviews/sessions/${sessionId}/identity-entities/decision`, body)
        return unwrapBody(res)
    },

    async patchHeftHarvestCuration(
        sessionId: string,
        objectId: string,
        body: { strengthenCandidate: boolean }
    ): Promise<{ sessionFill: import('@/modules/interview-coach/session-fill-types').SessionFillView }> {
        const res = await NodeAxios.patch(
            `/live-interviews/sessions/${sessionId}/session-fill/contributions/${encodeURIComponent(objectId)}/curation`,
            body
        )
        return unwrapBody(res)
    },

    async confirmSessionFillHeftContribution(
        sessionId: string,
        objectId: string,
        body: { statement: string; starTier?: 'primary' | 'supporting' }
    ): Promise<{ sessionFill: import('@/modules/interview-coach/session-fill-types').SessionFillView }> {
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/session-fill/contributions/${encodeURIComponent(objectId)}/confirm-heft`,
            body
        )
        return unwrapBody(res)
    },

    async mutateHeftHarvestCuration(
        sessionId: string,
        body: import('@/modules/interview-coach/heft/heft-curation-mutate.types').HeftCurationMutateInput
    ): Promise<import('@/modules/interview-coach/heft/heft-curation-mutate.types').HeftCurationMutateResult> {
        const res = await NodeAxios.post(
            `/live-interviews/sessions/${sessionId}/session-fill/heft-curation/mutate`,
            body
        )
        return unwrapBody(res)
    },

    async patchSessionFillConceptBinding(
        sessionId: string,
        objectId: string,
        body: {
            action: 'confirm' | 'correct' | 'reject' | 'leave_unresolved'
            selectedConceptRef?: string
            expectedProposalRevision: string
            idempotencyKey: string
        }
    ): Promise<unknown> {
        const res = await NodeAxios.patch(
            `/live-interviews/sessions/${sessionId}/session-fill/contributions/${encodeURIComponent(objectId)}/concept-binding`,
            body
        )
        return unwrapBody(res)
    },

    async getSessionDomainAlignmentProposals(sessionId: string): Promise<{
        proposals: Array<{
            proposalId: string
            sourceObjectRef: string
            proposalRevision: string
            recommendedConceptRef?: string
            disposition: string
            candidates: Array<{
                conceptRef: string
                confidence: number
                rationale: string
                breadcrumbLabels?: string[]
            }>
        }>
    }> {
        const res = await NodeAxios.get(`/interviews/${sessionId}/domain-alignment-proposals`)
        return unwrapBody(res)
    },

    async searchDomainConcepts(
        query: string,
        limit = 20
    ): Promise<{
        results: Array<{
            conceptId: string
            label: string
            conceptKind: string
            source: string
            breadcrumbLabels: string[]
        }>
    }> {
        const res = await NodeAxios.get('/domain-concepts', { params: { q: query, limit } })
        return unwrapBody(res)
    },
}
